import { useState } from 'react';
import PageApp from '../../../Page';
import { HomeOutlined, UnorderedListOutlined, FileExcelOutlined, FilePdfOutlined, CalculatorOutlined } from '@ant-design/icons';
import { Button, Card, Form, Space, Table, Alert, Divider, Empty, message } from 'antd';
import AnioPeriodo from '../../../../components/forms/anio_periodo';
import { getByPeriodo, calcular, exportXlsx, exportPdf } from '../../../../apis/DistribucionEfectivoApi';
import { status_period } from '@app/constants/period';

const routes = [{
  href: '',
  title: <>
    <HomeOutlined />
    <span>Inicio</span>
  </>,
}, {
  href: '',
  title: (
    <>
      <UnorderedListOutlined />
      <span>Reportes</span>
    </>
  ),
}, {
  href: '',
  title: (
    <>
      <UnorderedListOutlined />
      <span>Distribución de Efectivo</span>
    </>
  ),
}];

const DistribucionEfectivo = () => {
  const [form] = Form.useForm();
  const [detalleId, setDetalleId] = useState(null);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState(null);

  const periodoListo = (period?.status ?? 0) >= status_period.cerrado;

  const load = async (id) => {
    const response = await getByPeriodo(id);
    if (response.ok) {
      setPayload(response.payload);
    } else {
      setPayload(null);
    }
  };

  const onBlurHeader = async (values, period) => {
    setPeriod(period);
    if (period?.periodoCompleto?.id) {
      setDetalleId(period.periodoCompleto.id);
      await load(period.periodoCompleto.id);
    } else {
      setDetalleId(null);
      setPayload(null);
    }
  };

  const handleCalcular = async () => {
    if (!detalleId) {
      message.error('Selecciona un periodo válido');
      return;
    }
    setLoading(true);
    try {
      const response = await calcular(detalleId);
      if (response.ok) {
        setPayload(response.payload);
        message.success('Distribución calculada correctamente');
      }
    } finally {
      setLoading(false);
    }
  };

  const fmtMoney = (value) =>
    Number(value || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fmtDenom = (n) => {
    const s = Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return s.replace(/\.?0+$/, '');
  };

  const denominaciones = payload?.tipos_efectivo || [];
  const filas = payload?.filas || [];
  const totales = payload?.totales || { piezas: {}, salario_neto: 0, sobrante: 0, total: 0 };

  const columns = [
    {
      title: 'No. Empleado',
      dataIndex: 'nomina_employee_number',
      key: 'nomina_employee_number',
      width: 120,
      sorter: (a, b) => (Number(a.nomina_employee_number) || 0) - (Number(b.nomina_employee_number) || 0),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Salario Neto',
      key: 'salario_neto',
      align: 'right',
      width: 130,
      render: (_, fila) => <span>{fmtMoney(fila.salario_neto)}</span>,
    },
    ...denominaciones.map((denominacion) => ({
      title: `$${fmtDenom(denominacion.denominacion)}`,
      key: `den_${denominacion.id}`,
      align: 'right',
      width: 90,
      render: (_, fila) => <span>{(fila.distribucion || {})[denominacion.id] || 0}</span>,
    })),
    {
      title: 'Total',
      key: 'total',
      align: 'right',
      width: 130,
      render: (_, fila) => <span>{fmtMoney(fila.total)}</span>,
    },
    {
      title: 'Sobrante',
      key: 'sobrante',
      align: 'right',
      width: 130,
      render: (_, fila) => <span>{fmtMoney(fila.sobrante)}</span>,
    },
  ];

  const summary = () => (
    <Table.Summary.Row style={{ background: '#fafafa' }}>
      <Table.Summary.Cell index={0} colSpan={2}>
        <strong>TOTAL</strong>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={2} align="right">
        <strong>{fmtMoney(totales.salario_neto)}</strong>
      </Table.Summary.Cell>
      {denominaciones.map((denominacion, index) => (
        <Table.Summary.Cell key={denominacion.id} index={3 + index} align="right">
          <strong>{(totales.piezas || {})[denominacion.id] || 0}</strong>
        </Table.Summary.Cell>
      ))}
      <Table.Summary.Cell index={3 + denominaciones.length} align="right">
        <strong>{fmtMoney(totales.total)}</strong>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={4 + denominaciones.length} align="right">
        <strong>{fmtMoney(totales.sobrante)}</strong>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );

  return (
    <PageApp routes={routes} title={'Distribución de Efectivo'}>
      <AnioPeriodo onBlur={onBlurHeader} form={form} disabledForm={false} />
      <Divider />
      <Space>
        <Button
          type="primary"
          icon={<CalculatorOutlined />}
          disabled={!periodoListo || !detalleId}
          loading={loading}
          onClick={handleCalcular}
        >
          Calcular
        </Button>
      </Space>
      {detalleId && !periodoListo && (
        <Alert
          style={{ marginTop: 16 }}
          type="warning"
          showIcon
          message="El periodo debe estar en estado Cerrado (o superior) para calcular la distribución de efectivo"
        />
      )}
      <Card
        size="small"
        title="Distribución de efectivo"
        style={{ marginTop: 16 }}
        extra={
          <Space>
            <Button icon={<FileExcelOutlined />} disabled={!payload} onClick={() => detalleId && exportXlsx(detalleId)}>
              Excel
            </Button>
            <Button icon={<FilePdfOutlined />} disabled={!payload} onClick={() => detalleId && exportPdf(detalleId)}>
              PDF
            </Button>
          </Space>
        }
      >
        {filas.length === 0 ? (
          <Empty
            description={
              detalleId
                ? 'No hay distribución calculada para este periodo'
                : 'Selecciona un periodo para visualizar la distribución'
            }
          />
        ) : (
          <Table
            dataSource={filas}
            columns={columns}
            rowKey="id"
            summary={summary}
            scroll={{ x: 'max-content' }}
          />
        )}
      </Card>
    </PageApp>
  );
};

export default DistribucionEfectivo;